<template>
    <el-container
        class="subproperty-dashboard app-content-page"
    >
        <el-row class="subproperty-dashboard__title">
            <el-col :span="24">
                <app-i18n code="common.overview" />
            </el-col>
        </el-row>

        <el-row
            class="subproperty-dashboard__container"
            justify="center"
            type="flex"
            v-loading="loading"
        >
            <el-col
                v-for="(stat, i) in statsArray"
                :key="`subproperty-dashboard__col--${i}`"
                :span="8"
            >
                <el-card
                    class="subproperty-dashboard__card"
                    :class="{ 'subproperty-dashboard__card--empty': !stat.count}"
                    :style="{ borderColor: stat.borderColor }"
                >
                    <span
                        class="subproperty-dashboard__card__icon icon--circle"
                        :class="stat.iconClass"
                        :style="{
                            backgroundColor: stat.iconBackgroundColor,
                            color: stat.iconColor
                        }"
                    />

                    <el-tooltip
                        :content="stat.tooltipCaption"
                        placement="bottom"
                        class="subproperty-dashboard__card__tooltip"
                    >
                        <font-awesome-icon
                            :icon="tooltipIcon"
                            size="1x"
                        />
                    </el-tooltip>

                    <template v-if="!!stat.count">
                        <p
                            class="subproperty-dashboard__card__count"
                            v-text="stat.count"
                        />
                        <p
                            class="subproperty-dashboard__card__title"
                            v-text="stat.title"
                        />
                    </template>

                    <template v-else>
                        <p
                            class="subproperty-dashboard__card__title"
                            v-text="stat.emptyCaption"
                        />
                    </template>
                </el-card>
            </el-col>
        </el-row>

        <el-row
            class="subproperty-dashboard__programs"
            v-if="stepsLeft === 0"
            v-loading="programsLoading"
        >
            <el-col
                :span="16"
                class="subproperty-dashboard__programs__title"
            >
                <app-i18n :code="!isPropertyNBA ? 'entities.program.label.many' : i18n('entities.season.label')" />
            </el-col>
            <el-col
                :span="8"
                align="right"
                class="subproperty-dashboard__programs__button"
            >
                <template v-if="!programsLoading">
                    <router-link
                        v-if="!!programs.length"
                        :to="{ name: 'programList' }"
                    >
                        <el-button type="secondary">
                            <app-i18n code="common.seeAll" />
                        </el-button>
                    </router-link>
                    <router-link
                        v-else
                        :to="{ name: 'programNew' }"
                    >
                        <el-button type="primary">
                            <app-i18n :code="!isPropertyNBA ? 'entities.program.new.title' : 'entities.season.new.title'" />
                        </el-button>
                    </router-link>
                </template>
            </el-col>
            <app-program-list-filter />
            <app-program-dashboard-table
                v-if="!!programs && !!programs.length"
                simple
                :max-items="3"
            />
        </el-row>
    </el-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { i18n } from '@shared/i18n';
import ProgramListFilter from '@modules/program/components/program-list-filter.vue';
import ProgramDashboardTable from '@modules/program/components/program-dashboard-table.vue';

export default {
    name: 'SubpropertyDashboard',
    data() {
        return {
            tooltipIcon: faQuestionCircle
        };
    },
    computed: {
        ...mapGetters({
            currentManage: 'auth/manage/currentManage',
            stats: 'subproperty/dashboard/stats',
            statsArray: 'subproperty/dashboard/statsArray',
            loading: 'subproperty/dashboard/loading',
            programs: 'program/list/rows',
            programsLoading: 'program/list/loading',
            isPropertyNBA: 'auth/manage/isPropertyNBA',
            steps: 'subproperty/requirements/steps',
        }),
        subpropertyId() {
            return this.currentManage.subproperty.id;
        },
        stepsList() {
            return Object.values(this.steps).filter(step => step.active);
        },
        doneSteps() {
            return this.stepsList.filter(step => step.done);
        },
        stepsTotal() {
            return this.stepsList.length;
        },
        stepsLeft() {
            return this.stepsTotal - this.doneSteps.length;
        },
    },
    mounted() {
        const { subpropertyId } = this;
        this.doFetch({ subpropertyId });
    },
    methods: {
      ...mapActions('subproperty/dashboard', ['doFetch']),
      i18n(code) {
        return i18n(code);
      },
    },
    components: {
        [ProgramListFilter.name]: ProgramListFilter,
        [ProgramDashboardTable.name]: ProgramDashboardTable,
    },
}
</script>

<style scoped>
.subproperty-dashboard {
    padding: 0;
    flex-direction: column;
}

.subproperty-dashboard p {
    margin: 0;
}

.subproperty-dashboard__container {
    width: 100%;
    padding: 30px 30px 22px 40px;
    flex-wrap: wrap;
}

.subproperty-dashboard__title {
    padding: 30px 54px 22px;
    font-size: 14px;
    letter-spacing: 0.2px;
    color: #666666;
    border-bottom: 1px solid #cccccc;
}

.subproperty-dashboard__card {
    border: 0;
    border-bottom: 3px solid transparent;
    border-radius: 0;
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
    height: 164px;
    margin: 12.5px;
}

.subproperty-dashboard__card__tooltip {
    grid-area: tooltip;
}

.subproperty-dashboard__card__icon {
    grid-area: icon;
    justify-self: center;
}

.subproperty-dashboard__card__title {
    grid-area: title;
    font-size: 14px;
    font-weight: 300;
    text-align: center;
    color: rgba(64, 64, 64, 0.35);
}

.subproperty-dashboard__card__count {
    grid-area: count;
    font-size: 40px;
    font-weight: bold;
    letter-spacing: 1px;
    text-align: center;
}

.subproperty-dashboard__programs {
    /* margin: 30px 40px 0 54px; */
    margin: 30px 54px 40px 54px;
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
}

.subproperty-dashboard__programs__title {
    font-size: 20px;
    color: #282d32;
    padding: 25px 40px 15px;
    height: 80px;
    display: flex;
    align-items: center;
}

.subproperty-dashboard__programs__button {
    padding: 25px 30px 15px 40px;
    height: 80px;
}
</style>

<style>
.subproperty-dashboard__card .el-card__body {
    display: grid;
    grid-template-columns: 20px 1fr 20px;
    grid-template-rows: 50px 45px 20px;
    grid-template-areas:
        'arrow icon tooltip'
        'count count count'
        'title title title';
    padding: 10px;
}

.subproperty-dashboard__card--empty .el-card__body {
    grid-template-rows: 45px 45px 20px;
    grid-template-areas:
        'arrow . tooltip'
        'icon icon icon'
        'title title title';
}
</style>

